import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { SparkleIcon as Sparkle, XIcon as X, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";
import { WaxSeal } from "@/components/luxury";

/**
 * One-time announcement strip nudging verified kennels to embed the
 * WaxSeal on their own website. Renders only when:
 *   • the calling user owns a verified kennel (`/breeders/mine` returns
 *     `{verified: true}`),
 *   • the user hasn't dismissed the strip before.
 * Dismissal is stored in localStorage so it doesn't return after one tap.
 */
const DISMISS_KEY = "ak9_seal_kit_announce_dismissed_v1";

export default function VerifiedSealKitAnnouncement() {
  const [breeder, setBreeder] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    if (dismissed) return;
    api.get("/breeders/mine")
      .then((r) => setBreeder(r.data?.id ? r.data : null))
      .catch(() => setBreeder(null));
  }, [dismissed]);

  if (dismissed || !breeder || !breeder.verified) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className="relative bg-cream border border-[#E2DAC2] rounded-xl p-5 md:p-6 mb-6 overflow-hidden"
      data-testid="seal-kit-announcement"
    >
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-3 right-3 text-[#6B6B6B] hover:text-ink transition-colors"
        aria-label="Dismiss"
        data-testid="dismiss-seal-kit-announcement"
      >
        <X size={16} weight="bold" />
      </button>
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex-shrink-0">
          <WaxSeal size={42} title="Verified" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-1 flex items-center gap-1.5">
            <Sparkle size={10} weight="fill" /> New
          </p>
          <h3 className="font-spectral text-xl tracking-tight">Your seal is live — drop it on your website</h3>
          <p className="text-sm text-[#3a3a3a] mt-1 max-w-2xl leading-relaxed">
            Two PNG variants, served live from our servers, plus a copy-paste
            HTML snippet. If your seal is ever revoked, the embed breaks
            visibly — buyers can trust the badge wherever they see it.
          </p>
        </div>
        <Link
          to={`/breeders/${breeder.id}#embed`}
          className="inline-flex items-center gap-1.5 rounded-full bg-ink hover:bg-crimson text-white text-sm font-medium px-5 py-2.5 transition-colors"
          data-testid="seal-kit-cta"
        >
          Open the embed kit
          <ArrowRight size={14} weight="bold" />
        </Link>
      </div>
    </div>
  );
}
